import React from 'react'
import moment from 'moment'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  ButtonContainer,
  Card,
  Col,
  DetailList,
  FormInput,
  FormInputText,
  FormInputDate,
  FormInputSelect,
  FormInputTextArea,
  Row
} from 'camino-react'

import { cloudhelpPost, cloudhelpPatch } from 'Utilities/CloudHelp'

class GenericTimesheetForm extends React.Component {
  constructor (props) {
    super(props)

    const { timesheet } = props

    this.state = {
      fields: {
        start_time: moment(timesheet.start_time).toDate(),
        end_time: moment(timesheet.end_time).toDate(),
        description: timesheet.description,
        comments: timesheet.comments,
        chargeable: timesheet.chargeable,
        work_type_id: timesheet.work_type_id,
        work_role_id: timesheet.work_role_id,
        utilization_deductable_minutes: timesheet.utilization_deductable_minutes
      },
      submitButton: {
        disabled: false,
        text: 'Save timesheet'
      },
      errors: []
    }
  }

  getFieldErrorClasses (field) {
    const { fieldsWithErrors } = this.state

    if (_.includes(fieldsWithErrors, field)) {
      return ('error')
    }

    return ''
  }

  handleFieldValue = (fieldName, value) => {
    this.setState((prevState) => {
      const { fields } = prevState
      const newFields = Object.assign({}, fields, { [fieldName]: value })

      return (
        { fields: newFields }
      )
    })
  }

  submit = () => {
    const { paths, timesheet: { id } } = this.props
    const { timesheetsPath } = paths
    const {
      fields: {
        description,
        start_time,
        end_time,
        comments,
        chargeable,
        work_type_id,
        work_role_id,
        utilization_deductable_minutes
      }
    } = this.state

    this.setState({
      submitButton: {
        disabled: true,
        text: 'Saving timesheet'
      }
    })

    const postData = {
      timesheet: {
        description,
        start_time,
        end_time,
        comments,
        chargeable,
        work_type_id,
        work_role_id,
        utilization_deductable_minutes
      }
    }

    const action = _.isNil(id) ? cloudhelpPost : cloudhelpPatch

    action(timesheetsPath, postData)
      .then((response) => {
        this.setState({
          submitButton: {
            disabled: false,
            text: 'Timesheet saved'
          }
        })

        window.location.href = response.data.redirect_url
      })
      .catch((error) => {
        const { data: errorData } = error.response
        const { errors } = errorData
        const errorMessageArray = _.map(errors, (errorText, key) => `${_.startCase(key)} ${errorText}`)

        this.setState({
          errors: errorMessageArray,
          submitButton: {
            disabled: false,
            text: 'Save timesheet'
          }
        })
      })
  }

  render () {
    const {
      submit
    } = this

    const {
      paths,
      workTypeOptions,
      workRoleOptions
    } = this.props

    const {
      alert,
      fields: {
        work_type_id,
        work_role_id,
        start_time,
        end_time,
        utilization_deductable_minutes
      },
      fields,
      errors,
      submitButton
    } = this.state

    const { disabled: submitDisabled, text: submitText } = submitButton

    const { timesheetsPath } = paths

    const textArea = key => ({
      value: fields[key],
      onValue: value => this.handleFieldValue(key, value),
      rows: '4',
      errors: _.includes(errors, key) && ['Check this field']
    })

    const selectedWorkRole = _.find(workRoleOptions, { value: work_role_id })
    const selectedWorkType = _.find(workTypeOptions, { value: work_type_id })

    return (
      <Row>
        <Col>
          { _.some(errors) && (
            <Alert alertType='error' title='Timesheet could not be submitted for the following reasons:'>
              {errors}
            </Alert>
          ) }
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Start and end time of the timesheet'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput label="Start time">
                  <FormInputDate
                    enableTime
                    onValue={date => this.handleFieldValue('start_time', date)}
                    value={start_time}
                  />
                </FormInput>
                <FormInput label="End time">
                  <FormInputDate
                    enableTime
                    onValue={date => this.handleFieldValue('end_time', date)}
                    value={end_time}
                  />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Description and comments',
                    content: 'Describe what the time was spent doing, or if you have any comments around it.'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput label="Description">
                  <FormInputTextArea {...textArea('description')} />
                </FormInput>
                <FormInput label="Comments (Optional)">
                  <FormInputTextArea {...textArea('comments')} />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Work type and role',
                    content: 'Select the type of work and role this timesheet entailed.'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput label="Work Type">
                  <FormInputSelect
                    value={selectedWorkType}
                    options={workTypeOptions}
                    onValue={value => (this.handleFieldValue('work_type_id', value))}
                    errors={_.find(errors, 'work_type_id') && ['Check this field']}
                  />
                </FormInput>
                <FormInput label="Work Role">
                  <FormInputSelect
                    value={selectedWorkRole}
                    options={workRoleOptions}
                    onValue={value => (this.handleFieldValue('work_role_id', value))}
                    errors={_.find(errors, 'work_role_id') && ['Check this field']}
                  />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Utilization deductable minutes',
                    content: 'Time that you dont want to appear in your daily utilization'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput label="Deductable minutes">
                  <FormInputText {...textArea('utilization_deductable_minutes')} />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <ButtonContainer center spaced>
                <a href={timesheetsPath} className="button button--outline">
                  Cancel
                </a>
                <Button disabled={submitDisabled} onClick={() => submit()}>
                  {submitText}
                </Button>
              </ButtonContainer>
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }
}

GenericTimesheetForm.propTypes = {
  paths: PropTypes.shape({
    timesheetsPath: PropTypes.string.isRequired
  }).isRequired,
  workTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })
  ).isRequired,
  workRoleOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })
  ).isRequired
}

export default GenericTimesheetForm
